/**
 * Assignment/Swap Popover Component
 * Shows assignment or swap details when clicking on a timeline bar
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { Typogaphy, Button } from 'vnetwork-ui'
import {
    X,
    Clock,
    Calendar,
    User,
    ArrowRight,
    MessageSquare,
    Bot,
} from 'lucide-react'

import { TimelineBarItem } from '../../../../types/timeline'
import {
    getStatusColor,
    getStatusBgColor,
    getAssignmentSource,
    getSourceColor,
    getSourceBgColor,
} from '../../../../constants'
import { generatePathDatacentral, paths } from '@/utils/path'

interface AssignmentPopoverProps {
    item: TimelineBarItem | null
    position: { x: number; y: number } | null
    onClose: () => void
    onViewDetail?: (item: TimelineBarItem) => void
}

const AssignmentPopover: React.FC<AssignmentPopoverProps> = ({
    item,
    position,
    onClose,
    onViewDetail,
}) => {
    const { t: translate } = useTranslation('oncall')
    const navigate = useNavigate()

    if (!item || !position) return null

    const isSwap = !!item.swapId
    const startTime = dayjs(item.value[1])
    const endTime = dayjs(item.value[2])

    // Calculate popover position (adjust to stay within viewport)
    const popoverStyle: React.CSSProperties = {
        position: 'fixed',
        left: Math.min(position.x, window.innerWidth - 320),
        top: Math.min(position.y + 10, window.innerHeight - 350),
        zIndex: 1000,
    }

    const handleViewSwapDetail = () => {
        onClose()
        if (item.swapId) {
            navigate(
                generatePathDatacentral(paths.oncallSwapDetail, {
                    id: item.swapId,
                })
            )
        }
    }

    return (
        <>
            {/* Backdrop */}
            <div className="fixed inset-0 z-[999]" onClick={onClose} />

            {/* Popover */}
            <div
                style={popoverStyle}
                className="bg-white rounded-lg shadow-xl border border-gray-200 w-[300px] overflow-hidden"
            >
                {/* Header */}
                <div
                    className="px-4 py-3 flex items-center justify-between"
                    style={{ backgroundColor: item.itemStyle.color }}
                >
                    <div className="flex items-center gap-2 text-white">
                        <User className="w-4 h-4" />
                        <Typogaphy
                            size="small"
                            className="font-semibold text-white"
                        >
                            {isSwap
                                ? `Swap Request #${item.swapId}`
                                : item.employeeName}
                        </Typogaphy>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-white/80 hover:text-white transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-4 space-y-3">
                    {/* Swap-specific info */}
                    {isSwap && (
                        <>
                            {/* From -> To */}
                            <div className="flex items-center gap-2 text-sm">
                                <User className="w-4 h-4 text-gray-400" />
                                <span className="text-gray-600">
                                    {item.requesterName}
                                </span>
                                <ArrowRight className="w-4 h-4 text-gray-400" />
                                <span className="text-gray-600 font-medium">
                                    {item.employeeName}
                                </span>
                            </div>

                            {/* Swap Reason */}
                            {item.swapReason && (
                                <div className="flex items-start gap-2 text-sm">
                                    <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5" />
                                    <span className="text-gray-600 italic">
                                        "{item.swapReason}"
                                    </span>
                                </div>
                            )}
                        </>
                    )}

                    {/* Employee (only for assignments, not swaps) */}
                    {!isSwap && (
                        <div className="flex items-center gap-2 text-sm">
                            <User className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-600 font-medium">
                                {item.employeeName}
                            </span>
                        </div>
                    )}

                    {/* Schedule */}
                    {item.scheduleName && (
                        <div className="flex items-center gap-2 text-sm">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-600">
                                {item.scheduleName}
                            </span>
                        </div>
                    )}

                    {/* Date */}
                    <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">
                            {startTime.format('DD/MM/YYYY')}
                        </span>
                    </div>

                    {/* Time */}
                    <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">
                            {startTime.format('HH:mm')} -{' '}
                            {endTime.format('HH:mm')}
                        </span>
                    </div>

                    {/* Layer & Status */}
                    <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 capitalize">
                            {item.layer === 'override'
                                ? translate('swap')
                                : translate('scheduled')}
                        </span>
                        <span
                            className="text-xs px-2 py-1 rounded"
                            style={{
                                color: getStatusColor(
                                    isSwap ? item.swapStatus || '' : item.status
                                ),
                                backgroundColor: getStatusBgColor(
                                    isSwap ? item.swapStatus || '' : item.status
                                ),
                            }}
                        >
                            {translate(
                                isSwap ? item.swapStatus || '' : item.status
                            )}
                        </span>
                        {/* Source indicator */}
                        {!isSwap && (
                            <span
                                className="text-xs px-2 py-1 rounded flex items-center gap-1"
                                style={{
                                    color: getSourceColor(
                                        getAssignmentSource(item.createdBy)
                                    ),
                                    backgroundColor: getSourceBgColor(
                                        getAssignmentSource(item.createdBy)
                                    ),
                                }}
                            >
                                {item.createdBy === null ? (
                                    <>
                                        <Bot className="w-3.5 h-3.5" />
                                        {translate('auto')}
                                    </>
                                ) : (
                                    <>
                                        <User className="w-3.5 h-3.5" />
                                        {item.createdByName ||
                                            translate('manual')}
                                    </>
                                )}
                            </span>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex gap-2">
                    {isSwap ? (
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleViewSwapDetail}
                            className="flex-1"
                        >
                            {translate('view_swap_detail')}
                        </Button>
                    ) : (
                        onViewDetail && (
                            <Button
                                variant="contained"
                                size="small"
                                onClick={() => onViewDetail(item)}
                                className="flex-1"
                            >
                                {translate('view_detail')}
                            </Button>
                        )
                    )}
                </div>
            </div>
        </>
    )
}

export default AssignmentPopover
